import { ComponentProps, forwardRef, SVGProps, useState } from 'react';
import { PopoverClose } from '@radix-ui/react-popover';
import { Matcher } from 'react-day-picker';
import {
  Button,
  Calendar,
  cn,
  containerVariants,
  Loader,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@sib/ui';
import { Calendar as CalendarIcon } from '@sib/ui/icons';
import { X } from '@sib/ui/icons';
import { format } from 'date-fns';
import { useDatetimePicker } from '../../../hooks';
import { TimePickerSelect } from './components';

interface ClearButtonProps extends ComponentProps<typeof Button> {
  label: string;
  containerClassName?: string;
}

interface TriggerButtonProps extends ComponentProps<typeof Button> {
  label?: string;
}

export type DatetimePickerProps = Omit<
  ComponentProps<typeof Calendar>,
  'selected' | 'onSelect' | 'mode' | 'disabled'
> & {
  withTime?: boolean;
  ButtonProps?: TriggerButtonProps;
  // TODO: handle all possible cases
  selected: Date | null;
  onSelect: (date: Date | null) => void;
  confirmLabel?: string;
  getTopLabel?: (selected: DatetimePickerProps['selected']) => string;
  dateFormat?: string;
  dateTimeFormat?: string;
  clearButtonProps?: ClearButtonProps;
  defaultDate?: Date | null;
  closeOnSelect?: boolean;
  disabled?: Matcher | Matcher[] | boolean;
  loading?: boolean;
  PopoverContentProps?: ComponentProps<typeof PopoverContent>;
  closeOnScroll?: boolean;
  PopoverTriggerProps?: ComponentProps<typeof PopoverTrigger>;
  IconProps?: SVGProps<SVGSVGElement>;
};

const DEFAULT_HOUR = 17;
const DEFAULT_DATE_FORMAT = 'PPP';
const DEFAULT_DATE_TIME_FORMAT = 'PPP p';

const DatetimePicker = forwardRef<HTMLButtonElement, DatetimePickerProps>(
  (props, ref) => {
    const {
      ButtonProps,
      IconProps,
      onSelect,
      selected,
      withTime,
      confirmLabel,
      getTopLabel,
      defaultMonth,
      dateTimeFormat = DEFAULT_DATE_TIME_FORMAT,
      dateFormat = DEFAULT_DATE_FORMAT,
      clearButtonProps,
      defaultDate = new Date(),
      closeOnSelect = false,
      disabled,
      loading,
      PopoverContentProps,
      closeOnScroll,
      PopoverTriggerProps,
      ...rest
    } = props;

    const { isOpen, setIsOpen, resolveDisabledProps } = useDatetimePicker({
      closeOnScroll,
    });
    const [isTimeViewActive, setIsTimeViewActive] = useState(false);

    const { isButtonDisabled, calendarDisabled } =
      resolveDisabledProps(disabled);

    const handleTimeChange = (type: 'hour' | 'minute', value: string) => {
      if (selected) {
        const newDate = new Date(selected);

        if (type === 'hour') newDate.setHours(parseInt(value));

        if (type === 'minute') newDate.setMinutes(parseInt(value));

        onSelect(newDate);
      }
    };

    const handleClear = () => {
      onSelect(null);
      setIsTimeViewActive(false);
    };
    const handleConfirm = () => {
      onSelect(selected);
      setIsOpen(false);
    };

    const mappedDefaultDate = defaultDate || undefined;

    return (
      <Popover
        modal
        open={isOpen}
        onOpenChange={open => {
          setIsOpen(open);
          if (!open) setIsTimeViewActive(false);
        }}
      >
        <PopoverTrigger
          asChild
          {...PopoverTriggerProps}
          className={cn('w-full', PopoverTriggerProps?.className)}
        >
          <Button
            ref={ref}
            {...ButtonProps}
            variant="ghost"
            disabled={ButtonProps?.disabled || isButtonDisabled || loading}
            className={cn(
              containerVariants({ variant: 'base' }),
              'text-primary items-center justify-between',
              !selected && 'text-muted-foreground',
              ButtonProps?.className
            )}
          >
            {selected ? (
              <span className="truncate">
                {format(selected, withTime ? dateTimeFormat : dateFormat)}
              </span>
            ) : (
              <span className="leading-6">{ButtonProps?.label}</span>
            )}
            {loading ? (
              <Loader
                {...IconProps}
                className={cn('size-5 shrink-0', IconProps?.className)}
              />
            ) : (
              <CalendarIcon
                {...IconProps}
                className={cn('size-5 shrink-0', IconProps?.className)}
              />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          {...PopoverContentProps}
          className={cn(
            'w-auto border-none p-0 shadow-[0px_24px_36px_0px_rgba(69,73,77,0.24)]',
            PopoverContentProps?.className
          )}
          onClick={e => e.stopPropagation()}
        >
          <PopoverClose className="text-primary hover:bg-surface-subtle absolute top-1 right-1 z-200 cursor-pointer p-2 hover:brightness-100">
            <X className="size-3" />
          </PopoverClose>
          <div className="sm:flex">
            {!isTimeViewActive ? (
              <Calendar
                {...rest}
                mode="single"
                selected={selected ?? undefined}
                defaultMonth={defaultMonth || mappedDefaultDate}
                disabled={calendarDisabled}
                onSelect={date => {
                  if (!date) return;

                  onSelect(
                    new Date(
                      date.getFullYear(),
                      date.getMonth(),
                      date.getDate(),
                      selected?.getHours() || DEFAULT_HOUR,
                      selected?.getMinutes() || 0
                    )
                  );

                  if (closeOnSelect) return setIsOpen(false);
                  if (withTime) setIsTimeViewActive(true);
                }}
              />
            ) : (
              <TimePickerSelect
                confirmLabel={confirmLabel ?? ''}
                handleTimeChange={handleTimeChange}
                selected={selected}
                setOpenedState={setIsOpen}
                onBackToDateSelection={() => setIsTimeViewActive(false)}
                topLabel={
                  selected
                    ? getTopLabel?.(selected) || format(selected, dateFormat)
                    : ''
                }
              />
            )}
          </div>
          <div className="flex justify-center gap-2 pb-2">
            {clearButtonProps?.label && (
              <div
                className={cn(
                  'flex justify-end',
                  clearButtonProps.containerClassName
                )}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!selected || clearButtonProps?.disabled}
                  {...clearButtonProps}
                  onClick={e => {
                    handleClear();
                    if (closeOnSelect) setIsOpen(false);
                    clearButtonProps?.onClick?.(e);
                  }}
                >
                  {clearButtonProps.label}
                </Button>
              </div>
            )}
            {confirmLabel && (
              <Button
                variant="default"
                size="sm"
                disabled={!selected}
                onClick={handleConfirm}
              >
                {confirmLabel}
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);

export { DatetimePicker };
